/**
 * "Showing N of M · Show all M" — the footer under a truncated list.
 *
 * Promoted from `sites/trade-panel.tsx` (#1011). It was a good component in
 * the wrong place: `assets/detail.tsx` re-implemented it twice and
 * `sites/detail.tsx` once, and the copies had already drifted — one said
 * "Show fewer" where the others said "Show less", and the photo grid's copy
 * hard-coded a different preview size, so the same control read differently on
 * two tabs of the same page.
 *
 * The `visible` prop exists because of that photo grid: a 12-thumbnail preview
 * is not the same as a 10-row table, and the original signature assumed one
 * constant. Passing the count in lets the caller keep its own cutoff without
 * forking the component — which is how the second copy came to exist.
 *
 * Returns null when there is nothing to expand, so no caller has to inline
 * that check.
 */

/** The default table cutoff. Callers with a different one pass `visible`. */
export const DEFAULT_VISIBLE_ROWS = 10;

export function ShowMoreFooter({
    total,
    expanded,
    onToggle,
    visible = DEFAULT_VISIBLE_ROWS,
    itemLabel,
}: {
    total: number;
    expanded: boolean;
    onToggle: () => void;
    /** How many rows are shown while collapsed. Defaults to 10. */
    visible?: number;
    /** Plural noun for the button, e.g. "documents". Omit for the bare count. */
    itemLabel?: string;
}) {
    if (total <= visible) return null;
    const suffix = itemLabel ? ` ${itemLabel}` : "";
    return (
        <div className="mt-3 flex items-center justify-between text-xs">
            <span className="text-foreground/80">
                Showing {expanded ? total : visible} of {total}
            </span>
            <button type="button" onClick={onToggle} className="font-medium text-primary hover:underline">
                {expanded ? "Show less" : `Show all ${total}${suffix}`}
            </button>
        </div>
    );
}
